import React from 'react';
import {useMatches} from '@remix-run/react';

import type {I18nLocale} from '~/lib/type';

/**
 * /pages/loyalty-program used to present its programme details as four baked
 * PNG tables (invisible to search engines, unreadable to screen readers).
 * These components re-create each of those images as real, crawlable HTML —
 * keyed by the ORIGINAL image asset ref, so Module.tsx can swap them in
 * exactly where the images sit in the Sanity page_sections. The duplicated
 * plain-text bullets that used to shadow the images were removed from the
 * Sanity document; the tables below carry the fuller data (point values,
 * $ amounts, voucher-conversion tiers) that only existed inside the PNGs.
 */

function useIsZh() {
  const [root] = useMatches();
  const selectedLocale = root?.data?.selectedLocale as I18nLocale | undefined;
  return selectedLocale?.language !== 'EN';
}

const PILL =
  'mb-4 w-full rounded bg-hk-deep-blue px-5 py-3 text-md font-semibold text-white md:text-lg';
const TABLE =
  'w-full border-collapse overflow-hidden rounded-lg text-sm md:text-md';
const ROW = 'border-b border-hk-deep-blue/10 bg-[#FDF6EC]';
const TH = 'w-2/5 px-4 py-3 text-left align-top font-semibold';
const TD = 'px-4 py-3 text-left md:text-center';

/** 專享會員優惠 — icon-grid summary image → text chips on a navy card. */
export function LoyaltyPerksGrid() {
  const isZh = useIsZh();
  const perks = isZh
    ? [
        '每週優惠',
        '迎新現金獎賞',
        '會員推薦獎賞',
        '生日獎賞',
        '現金券兌換',
        '車房折扣',
        '海外自駕禮遇',
        '新車禮遇',
      ]
    : [
        'Weekly offers',
        'Welcome cash reward',
        'Member referral reward',
        'Birthday reward',
        'Voucher redemption',
        'Garage discount',
        'Overseas riding perks',
        'New bike perks',
      ];
  return (
    <section className="my-6 rounded-xl bg-hk-deep-blue p-6 text-white md:p-8">
      <h2 className="mb-5 text-center text-lg font-bold tracking-wide md:text-xl">
        {isZh ? '專享會員優惠' : 'Member-only perks'}
      </h2>
      <ul className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {perks.map((perk) => (
          <li
            key={perk}
            className="rounded-full border border-white/30 px-3 py-2 text-center text-sm md:text-md"
          >
            {perk}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 新手任務 table image → welcome line + real table. */
export function LoyaltyNewbieTable() {
  const isZh = useIsZh();
  const rows: [string, string][] = isZh
    ? [
        ['追蹤我們的 FACEBOOK 專頁', '500 積分'],
        ['追蹤我們的 INSTAGRAM 專頁', '500 積分'],
        ['登記生日日期', '500 積分'],
        ['會員生日禮遇', '1,500 額外積分'],
        [
          '會員推薦獎賞',
          '1) 新會員使用推薦人分享的連結會獲得 $50 現金券 2) 新會員使用現金券下單後 3) 推薦人會獲得 1,500 積分',
        ],
        ['售後撰文點評', '$100 現金券（網購限定回贈）'],
      ]
    : [
        ['Follow our FACEBOOK page', '500 points'],
        ['Follow our INSTAGRAM page', '500 points'],
        ['Enter your birthday', '500 points'],
        ['Birthday treat', '1,500 bonus points'],
        [
          'Member referral reward',
          '1) A new member registering via your referral link gets a $50 cash voucher 2) once the new member orders with the voucher 3) you receive 1,500 points',
        ],
        ['Post-purchase review', '$100 cash voucher (online orders only)'],
      ];
  return (
    <section className="my-6">
      <p className="mb-4 text-md">
        {isZh
          ? '歡迎您！您可以輕鬆透過以下活動提升積分！'
          : 'Welcome! You can easily earn points through the activities below!'}
      </p>
      <h2 className={PILL}>{isZh ? '新手任務' : 'Rookie mission'}</h2>
      <table className={TABLE}>
        <tbody>
          {rows.map(([task, reward]) => (
            <tr key={task} className={ROW}>
              <th scope="row" className={TH}>
                {task}
              </th>
              <td className={TD}>{reward}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** 零售及車房服務 + 現金券一覽 table image → two real tables. */
export function LoyaltyRetailTable() {
  const isZh = useIsZh();
  const rows: [string, string][] = isZh
    ? [
        ['賺取積分', '透過消費每 $1 賺取 1 積分'],
        ['賺取額外積分', '逢週四 零售消費滿港幣 $1000 多送 1000 積分（2025新增項目）'],
        ['迎新優惠（一次性）', '$100 現金券'],
      ]
    : [
        ['Earn points', 'Earn 1 point for every $1 spent'],
        [
          'Earn bonus points',
          'Every Thursday, spend HK$1,000 or more in retail and receive 1,000 bonus points (new for 2025)',
        ],
        ['Welcome offer (one-time)', '$100 cash voucher'],
      ];
  const tiers = {
    points: ['1,000', '2,000', '4,000', '6,000', '8,000', '10,000'],
    voucher: ['$50', '$100', '$200', '$300', '$400', '$500'],
    minSpend: ['$100', '$200', '$400', '$600', '$800', '$1,000'],
  };
  return (
    <section className="my-6">
      <h2 className={PILL}>
        {isZh
          ? '四千尺旗艦門市及電單車維修中心'
          : 'Retail & 26KING garage service'}
      </h2>
      <table className={TABLE}>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label} className={ROW}>
              <th scope="row" className={TH}>
                {label}
              </th>
              <td className={TD}>{value}</td>
            </tr>
          ))}
          <tr className={ROW}>
            <td colSpan={2} className="px-4 py-3 text-center font-medium">
              {isZh
                ? '到會員專區以所持積分免費兌換各式各樣人身部品'
                : 'Redeem all kinds of riding gear for free with your points in the member portal'}
            </td>
          </tr>
        </tbody>
      </table>
      <h3 className="mb-3 mt-6 text-md font-bold md:text-lg">
        {isZh ? '現金券一覽' : 'Cash-voucher tiers'}
      </h3>
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <tbody>
            <tr className={ROW}>
              <th scope="row" className="px-3 py-3 text-left font-semibold">
                {isZh ? '會員積分' : 'Points'}
              </th>
              {tiers.points.map((v) => (
                <td key={v} className="px-3 py-3 text-center">
                  {v}
                </td>
              ))}
            </tr>
            <tr className={ROW}>
              <th scope="row" className="px-3 py-3 text-left font-semibold">
                {isZh ? '現金券' : 'Cash voucher'}
              </th>
              {tiers.voucher.map((v) => (
                <td key={v} className="px-3 py-3 text-center">
                  {v}
                </td>
              ))}
            </tr>
            <tr className={ROW}>
              <th scope="row" className="px-3 py-3 text-left font-semibold">
                {isZh ? '最低消費額' : 'Minimum spend'}
              </th>
              {tiers.minSpend.map((v) => (
                <td key={v} className="px-3 py-3 text-center">
                  {v}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** 二輪冒險 / 26KING / RENTALBIKE.HK $200 回贈 table image → real table with links. */
export function LoyaltyPartnersTable() {
  const isZh = useIsZh();
  const rows = isZh
    ? [
        {
          name: '二輪冒險・電單車旅行團',
          reward: '購物現金券回贈（首次報團適用）',
          amount: '$200',
          url: 'https://www.helmetking.com/pages/motorcycle-guided-tour/?utm_source=hmk-website',
        },
        {
          name: '26KING・全新行貨車銷售、二手電單車寄賣及回收服務',
          reward: '購物現金券回贈',
          amount: '$200',
          url: 'https://www.26king.hk/',
        },
        {
          name: 'RENTALBIKE.HK・香港全新租電單車平台',
          reward: '購物現金券回贈（首次租車適用）',
          amount: '$200',
          url: 'https://rentalbike.hk/',
        },
      ]
    : [
        {
          name: '26ADVENTURE overseas riding experience',
          reward: 'Shopping-voucher rebate (first tour)',
          amount: '$200',
          url: 'https://www.helmetking.com/pages/motorcycle-guided-tour/?utm_source=hmk-website',
        },
        {
          name: '26KING motorcycle dealership',
          reward: 'Shopping-voucher rebate',
          amount: '$200',
          url: 'https://www.26king.hk/',
        },
        {
          name: 'RENTALBIKE.HK・HK motorcycle rental',
          reward: 'Shopping-voucher rebate (first rental)',
          amount: '$200',
          url: 'https://rentalbike.hk/',
        },
      ];
  return (
    <section className="my-6">
      <h2 className={PILL}>
        {isZh ? '集團業務會員禮遇' : 'Group-business member rewards'}
      </h2>
      <table className={TABLE}>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className={ROW}>
              <th scope="row" className={TH}>
                <a
                  href={row.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline decoration-hk-orange decoration-2 underline-offset-4 transition-colors duration-200 hover:text-hk-orange"
                >
                  {row.name}
                </a>
              </th>
              <td className={TD}>{row.reward}</td>
              <td className="px-4 py-3 text-center font-semibold">
                {row.amount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * Original PNG asset ref → replacement component (zh_TW and en page_sections
 * each have their own upload of the same four graphics).
 */
export const LOYALTY_IMAGE_SWAPS: Record<string, React.FC> = {
  // zh_TW uploads
  'image-ef19c948445a884ae310a6630308a87e61a5e9a0-2160x1080-png':
    LoyaltyPerksGrid,
  'image-95b7089676a30fd789dd9d23aa64ac48f6616b6c-2160x1080-png':
    LoyaltyNewbieTable,
  'image-d9281c8d489cb1f2306d2bf4459d6cd45adbdbc8-2160x1080-png':
    LoyaltyRetailTable,
  'image-5406115858826f2dd2e2dc10da739ec667d4cc8f-2160x1080-png':
    LoyaltyPartnersTable,
  // en uploads
  'image-101118ecfe7e64c74f24a95e4708f6213897f2de-2160x1080-png':
    LoyaltyPerksGrid,
  'image-7131f42ce1d88557615e088ad9154775f73b75c3-2160x1080-png':
    LoyaltyNewbieTable,
  'image-dea5e99d99d354ec86b06790272002e97b67b63e-2160x1080-png':
    LoyaltyRetailTable,
  'image-fdabf47a9e82b3c40bd21b9b67d69c2751333908-2160x1080-png':
    LoyaltyPartnersTable,
};
